import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import api from '../lib/api';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { 
  Search, 
  Loader2,
  Mail,
  Phone,
  Building2,
  MapPin,
  Calendar,
  User,
  Briefcase,
  Heart,
  Hash,
  UserCircle,
  QrCode,
  Download,
  FileImage,
  FileText,
  ChevronDown,
  Eye,
  X,
  Clock,
  Star,
  Target,
  Linkedin,
  ExternalLink,
  Share2,
  Link,
  Copy,
  Check,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Plus,
  Settings
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

export default function DirectoryPage() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ departmentId: '', siteId: '' });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrEmployee, setQrEmployee] = useState(null);
  const [showDeptQRDialog, setShowDeptQRDialog] = useState(false);
  const [deptQRData, setDeptQRData] = useState(null);
  const [branding, setBranding] = useState({
    primaryColor: '#2054A6',
    accentColor: '#F97316',
    logoUrl: null,
    companyDisplayName: ''
  });
  const [exporting, setExporting] = useState(false);
  const directoryRef = useRef(null);
  
  // Share functionality
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [loadingShareLinks, setLoadingShareLinks] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [newShareConfig, setNewShareConfig] = useState({
    title: 'Trombinoscope',
    description: '',
    departmentId: '',
    siteId: '',
    showEmail: true,
    showPhone: true,
    expiresInDays: ''
  });

  useEffect(() => {
    fetchData();
    fetchBranding();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, deptRes, siteRes, societeRes] = await Promise.all([
        api.get('/employees/all'),
        api.get('/departments'),
        api.get('/sites'),
        api.get('/societes')
      ]);
      setEmployees((empRes.data || []).filter(e => e.status === 'Actif'));
      setDepartments(deptRes.data.departments || deptRes.data || []);
      setSites(siteRes.data || []);
      setSocietes(societeRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await api.get('/branding');
      setBranding(response.data);
    } catch (error) {
      console.error('Could not load branding:', error);
    }
  };

  // Share link management
  const fetchShareLinks = async () => {
    setLoadingShareLinks(true);
    try {
      const response = await api.get('/directory/share-links');
      setShareLinks(response.data.links || []);
    } catch (error) {
      console.error('Could not load share links:', error);
    } finally {
      setLoadingShareLinks(false);
    }
  };

  const openShareDialog = () => {
    setShowShareDialog(true);
    fetchShareLinks();
  };

  const createShareLink = async () => {
    setCreatingLink(true);
    try {
      const response = await api.post('/directory/share-links', {
        title: newShareConfig.title || 'Trombinoscope',
        description: newShareConfig.description || null,
        departmentId: newShareConfig.departmentId || null,
        siteId: newShareConfig.siteId || null,
        showEmail: newShareConfig.showEmail,
        showPhone: newShareConfig.showPhone,
        expiresInDays: newShareConfig.expiresInDays ? parseInt(newShareConfig.expiresInDays) : null
      });
      
      setShareLinks(prev => [response.data, ...prev]);
      toast.success('Lien de partage créé');
      
      // Reset form
      setNewShareConfig({
        title: 'Trombinoscope',
        description: '',
        departmentId: '',
        siteId: '',
        showEmail: true,
        showPhone: true,
        expiresInDays: ''
      });
    } catch (error) {
      toast.error('Erreur lors de la création du lien');
    } finally {
      setCreatingLink(false);
    }
  };

  const toggleShareLink = async (linkId) => {
    try {
      const response = await api.post(`/directory/share-links/${linkId}/toggle`);
      setShareLinks(prev => prev.map(link => 
        link.id === linkId ? { ...link, isActive: response.data.isActive } : link
      ));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const deleteShareLink = async (linkId) => {
    if (!window.confirm('Supprimer ce lien de partage ?')) return;
    
    try {
      await api.delete(`/directory/share-links/${linkId}`);
      setShareLinks(prev => prev.filter(link => link.id !== linkId));
      toast.success('Lien supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyShareLink = (link) => {
    const url = `${window.location.origin}/shared/directory/${link.token}`;
    navigator.clipboard.writeText(url);
    setCopiedLinkId(link.id);
    toast.success('Lien copié dans le presse-papier');
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  const getShareLinkUrl = (link) => {
    return `${window.location.origin}/shared/directory/${link.token}`;
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || '';
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || '';
  };

  const getSocieteName = (societeId) => {
    const societe = societes.find(s => s.id === societeId);
    return societe?.name || '';
  };

  const getInitials = (employee) => {
    return `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase();
  };

  const getManagerName = (managerId) => {
    if (!managerId) return null;
    const manager = employees.find(e => e.id === managerId);
    return manager ? `${manager.firstName} ${manager.lastName}` : null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const calculateAge = (dateNaissance) => {
    if (!dateNaissance) return null;
    try {
      const birth = new Date(dateNaissance);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age;
    } catch {
      return null;
    }
  };

  const calculateSeniority = (hireDate) => {
    if (!hireDate) return null;
    try {
      const hire = new Date(hireDate);
      const today = new Date();
      const years = today.getFullYear() - hire.getFullYear();
      const months = today.getMonth() - hire.getMonth();
      
      if (years === 0) {
        return months <= 1 ? `${months} mois` : `${months} mois`;
      } else if (years === 1 && months < 0) {
        return `${12 + months} mois`;
      } else {
        return years === 1 ? '1 an' : `${years} ans`;
      }
    } catch {
      return null;
    }
  };

  const getPublicUrl = () => {
    // Use current window origin for public URLs (frontend routes)
    return window.location.origin;
  };

  const openQRDialog = (employee) => {
    setQrEmployee(employee);
    setShowQRDialog(true);
  };

  const downloadQR = async (employee) => {
    try {
      const response = await api.get(`/qrcode/employee/${employee.id}/download?size=500`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr_${employee.firstName}_${employee.lastName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('QR Code téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const openDeptQRDialog = async (deptId) => {
    if (!deptId) {
      toast.error('Veuillez sélectionner un département');
      return;
    }
    
    try {
      const response = await api.get(`/qrcode/department/${deptId}?format=base64`);
      setDeptQRData({
        ...response.data,
        departmentName: getDepartmentName(deptId)
      });
      setShowDeptQRDialog(true);
    } catch (error) {
      toast.error('Erreur lors de la génération du QR Code');
    }
  };

  const downloadDeptQR = async (deptId) => {
    try {
      const response = await api.get(`/qrcode/department/${deptId}/download?size=500`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr_trombinoscope_${getDepartmentName(deptId).replace(/\s+/g, '_')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('QR Code téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleExportPDF = async () => {
    if (!directoryRef.current) return;
    setExporting(true);
    
    try {
      const element = directoryRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true
      });
      
      const imgWidth = 297; // A4 landscape width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF({
        orientation: imgHeight > imgWidth ? 'portrait' : 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`trombinoscope_${branding.companyDisplayName || 'entreprise'}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success('PDF exporté avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPNG = async () => {
    if (!directoryRef.current) return;
    setExporting(true);
    
    try {
      const element = directoryRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `trombinoscope_${branding.companyDisplayName || 'entreprise'}_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Image exportée avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  // Fonction pour trier les employés par hiérarchie
  // Directeur -> Manager/Responsable -> Chef -> Autres
  const sortByHierarchy = (employeesList) => {
    // Créer un map des employés par ID pour un accès rapide
    const employeeMap = new Map(employeesList.map(e => [e.id, e]));
    
    // Fonction pour obtenir la priorité basée sur le titre de poste
    const getPriorityFromTitle = (title) => {
      if (!title) return 99;
      const lowerTitle = title.toLowerCase();
      
      // Niveau 1: Directeurs (inclut directeur, directrice, DG, PDG, CEO, etc.)
      if (lowerTitle.includes('directeur') || lowerTitle.includes('directrice') || 
          lowerTitle.includes('dg') || lowerTitle.includes('pdg') || 
          lowerTitle.includes('ceo') || lowerTitle.includes('coo') || 
          lowerTitle.includes('cfo') || lowerTitle.includes('cto') ||
          lowerTitle.includes('président') || lowerTitle.includes('gérant')) return 1;
      
      // Niveau 2: Managers et Responsables
      if (lowerTitle.includes('manager') || lowerTitle.includes('responsable')) return 2;
      
      // Niveau 3: Chefs et Superviseurs
      if (lowerTitle.includes('chef') || lowerTitle.includes('superviseur') || 
          lowerTitle.includes('coordinateur') || lowerTitle.includes('head of')) return 3;
      
      // Niveau 4: Seniors et Leads
      if (lowerTitle.includes('senior') || lowerTitle.includes('principal') || 
          lowerTitle.includes('lead') || lowerTitle.includes('expert')) return 4;
      
      // Niveau 5: Assistants
      if (lowerTitle.includes('assistant') || lowerTitle.includes('adjoint')) return 5;
      
      // Niveau 6: Autres (opérateurs, techniciens, etc.)
      return 10;
    };
    
    // Calculer le niveau hiérarchique de chaque employé
    // Plus le nombre est petit, plus l'employé est haut dans la hiérarchie
    const getHierarchyLevel = (emp, visited = new Set()) => {
      if (visited.has(emp.id)) return 0; // Éviter les boucles
      visited.add(emp.id);
      
      if (!emp.managerId) return 0; // Pas de manager = niveau 0 (top)
      
      const manager = employeeMap.get(emp.managerId);
      if (!manager) return 1; // Manager non trouvé = niveau 1
      
      return getHierarchyLevel(manager, visited) + 1;
    };
    
    // Assigner un niveau à chaque employé et la priorité du titre
    const employeesWithLevel = employeesList.map(emp => ({
      ...emp,
      _hierarchyLevel: getHierarchyLevel(emp),
      _titlePriority: getPriorityFromTitle(emp.jobTitle)
    }));
    
    // Trier : d'abord par département, puis par priorité du titre, puis par niveau hiérarchique, puis par nom
    return employeesWithLevel.sort((a, b) => {
      // 1. Trier par département
      const deptA = getDepartmentName(a.departmentId) || 'ZZZ';
      const deptB = getDepartmentName(b.departmentId) || 'ZZZ';
      if (deptA !== deptB) return deptA.localeCompare(deptB);
      
      // 2. Trier par priorité du titre (Directeur/Directrice en premier)
      if (a._titlePriority !== b._titlePriority) {
        return a._titlePriority - b._titlePriority;
      }
      
      // 3. Trier par niveau hiérarchique (plus petit = plus haut)
      if (a._hierarchyLevel !== b._hierarchyLevel) {
        return a._hierarchyLevel - b._hierarchyLevel;
      }
      
      // 4. Trier par nom
      return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    });
  };

  const filteredEmployees = sortByHierarchy(employees.filter(emp => {
    const matchesSearch = !search || 
      `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      (emp.email || '').toLowerCase().includes(search.toLowerCase()) ||
      emp.jobTitle.toLowerCase().includes(search.toLowerCase());
    
    const matchesDept = !filters.departmentId || emp.departmentId === filters.departmentId;
    const matchesSite = !filters.siteId || emp.siteId === filters.siteId;

    return matchesSearch && matchesDept && matchesSite;
  }));

  if (loading) {
    return (
      <DashboardLayout title="Trombinoscope">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: branding.primaryColor }} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Trombinoscope">
      <div className="space-y-6" data-testid="directory-page">
        {/* Header with controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Trombinoscope
            </h1>
            <p className="text-slate-600">
              {filteredEmployees.length} collaborateur{filteredEmployees.length > 1 ? 's' : ''} actif{filteredEmployees.length > 1 ? 's' : ''}
            </p>
          </div>
          
          <div className="flex gap-2">
            {/* Share Button */}
            <Button
              variant="outline"
              onClick={openShareDialog}
              className="gap-2"
              data-testid="share-directory-btn"
            >
              <Share2 className="w-4 h-4" />
              Partager
            </Button>
            
            {/* QR Code Département Button - visible when department filtered */}
            {filters.departmentId && (
              <Button
                variant="outline"
                onClick={() => openDeptQRDialog(filters.departmentId)}
                className="gap-2"
              >
                <QrCode className="w-4 h-4" />
                QR Département
              </Button>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  disabled={exporting}
                  style={{ backgroundColor: branding.primaryColor }}
                  className="text-white"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Exporter
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportPDF}>
                  <FileText className="w-4 h-4 mr-2 text-red-600" />
                  Export PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPNG}>
                  <FileImage className="w-4 h-4 mr-2 text-green-600" />
                  Export PNG
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, email, poste..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="directory-search"
                />
              </div>
              
              <Select
                value={filters.departmentId || 'all'}
                onValueChange={(value) => setFilters(prev => ({ ...prev, departmentId: value === 'all' ? '' : value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Département" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les départements</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.siteId || 'all'}
                onValueChange={(value) => setFilters(prev => ({ ...prev, siteId: value === 'all' ? '' : value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les sites</SelectItem>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Trombinoscope Export View */}
        <div ref={directoryRef} className="bg-white p-6 rounded-lg">
          {/* Header with branding */}
          <div 
            className="rounded-t-lg p-6 mb-6"
            style={{ backgroundColor: branding.primaryColor }}
          >
            <div className="flex items-center justify-between">
              {branding.logoUrl ? (
                <div className="bg-white rounded-lg p-2">
                  <img 
                    src={branding.logoUrl} 
                    alt="Logo" 
                    className="h-10 max-w-[200px] object-contain"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-white">
                  <Building2 className="w-8 h-8" />
                  <span className="text-xl font-bold">
                    {branding.companyDisplayName || branding.companyName || 'Entreprise'}
                  </span>
                </div>
              )}
              <div className="text-white text-right">
                <h2 className="text-2xl font-bold">TROMBINOSCOPE</h2>
                <p className="text-white/80 text-sm">
                  {filters.departmentId 
                    ? getDepartmentName(filters.departmentId)
                    : 'Tous les collaborateurs'}
                </p>
              </div>
            </div>
            {/* Accent bar */}
            <div 
              className="h-1 mt-4 rounded-full"
              style={{ backgroundColor: branding.accentColor }}
            />
          </div>

          {/* Employee Grid - New Design */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredEmployees.map((employee) => (
              <div 
                key={employee.id}
                className="relative rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer group border-2"
                style={{ borderColor: `${branding.primaryColor}30` }}
                onClick={() => setSelectedEmployee(employee)}
              >
                {/* QR Code button overlay */}
                <button 
                  className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 rounded-full shadow-lg bg-white hover:bg-slate-100 flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    openQRDialog(employee);
                  }}
                  title="Voir le QR Code"
                >
                  <QrCode className="w-4 h-4 text-slate-600" />
                </button>
                
                {/* Photo section with white background */}
                <div className="bg-white p-4 flex justify-center">
                  <Avatar 
                    className="h-24 w-24 ring-4 ring-white shadow-lg"
                    style={{ backgroundColor: branding.primaryColor }}
                  >
                    {employee.photoUrl ? (
                      <AvatarImage src={employee.photoUrl} alt={`${employee.firstName} ${employee.lastName}`} />
                    ) : null}
                    <AvatarFallback 
                      className="text-white text-2xl font-bold"
                      style={{ backgroundColor: branding.primaryColor }}
                    >
                      {getInitials(employee)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                
                {/* Info section with primary color */}
                <div 
                  className="p-4 text-center text-white"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  <h3 className="font-bold text-lg uppercase tracking-wide">
                    {employee.firstName} {employee.lastName}
                  </h3>
                  <p className="text-white/90 text-sm mt-1 uppercase">
                    {employee.jobTitle}
                  </p>
                  {employee.departmentId && (
                    <p className="text-white/70 text-xs mt-2">
                      {getDepartmentName(employee.departmentId)}
                    </p>
                  )}
                  
                  {/* Accent bar at bottom */}
                  <div 
                    className="h-1.5 -mx-4 -mb-4 mt-4"
                    style={{ backgroundColor: branding.accentColor }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Empty state */}
          {filteredEmployees.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">Aucun collaborateur trouvé</p>
            </div>
          )}
          
          {/* Footer with company logo */}
          {branding.logoUrl && filteredEmployees.length > 0 && (
            <div className="mt-8 pt-6 border-t flex justify-center">
              <img 
                src={branding.logoUrl} 
                alt="Logo" 
                className="h-10 object-contain opacity-60"
              />
            </div>
          )}
        </div>

        {/* Employee Detail Dialog - Full Profile Style */}
        <Dialog open={!!selectedEmployee} onOpenChange={() => setSelectedEmployee(null)}>
          <DialogContent className="sm:max-w-xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
            {selectedEmployee && (
              <div className="relative">
                {/* Top Banner */}
                <div 
                  className="h-3"
                  style={{ backgroundColor: branding.primaryColor }}
                />
                
                <div className="p-6 pb-4">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
                    {/* Photo */}
                    <div className="flex-shrink-0 relative">
                      <div 
                        className="p-1 rounded-2xl"
                        style={{ background: `linear-gradient(135deg, ${branding.primaryColor}, ${branding.accentColor})` }}
                      >
                        <Avatar className="h-28 w-28 rounded-xl border-4 border-white">
                          {selectedEmployee.photoUrl ? (
                            <AvatarImage 
                              src={selectedEmployee.photoUrl} 
                              alt={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
                              className="object-cover rounded-xl"
                            />
                          ) : null}
                          <AvatarFallback 
                            className="text-white text-3xl font-bold rounded-xl"
                            style={{ backgroundColor: branding.primaryColor }}
                          >
                            {getInitials(selectedEmployee)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      
                      {/* QR Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openQRDialog(selectedEmployee);
                        }}
                        className="absolute -bottom-2 -right-2 p-2 rounded-full bg-white shadow-lg hover:shadow-xl transition-all"
                        style={{ border: `2px solid ${branding.primaryColor}` }}
                        title="QR Code"
                      >
                        <QrCode className="w-4 h-4" style={{ color: branding.primaryColor }} />
                      </button>
                    </div>

                    {/* Info */}
                    <div className="text-center sm:text-left flex-1">
                      <h1 className="text-2xl font-bold text-slate-900">
                        {selectedEmployee.firstName} {selectedEmployee.lastName}
                      </h1>
                      <p 
                        className="text-lg font-semibold mt-1"
                        style={{ color: branding.primaryColor }}
                      >
                        {selectedEmployee.jobTitle}
                      </p>
                      
                      {/* Organisation badges - Société, Département, Site */}
                      <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                        <Badge 
                          className="gap-1.5 px-3 py-1"
                          style={{ 
                            backgroundColor: `${branding.primaryColor}15`,
                            color: branding.primaryColor,
                            border: `1px solid ${branding.primaryColor}30`
                          }}
                        >
                          <Briefcase className="w-3.5 h-3.5" />
                          {getSocieteName(selectedEmployee.societeId) || branding.companyDisplayName || branding.companyName || 'Entreprise'}
                        </Badge>
                        {selectedEmployee.departmentId && (
                          <Badge 
                            className="gap-1.5 px-3 py-1"
                            style={{ 
                              backgroundColor: `${branding.accentColor}15`,
                              color: branding.accentColor,
                              border: `1px solid ${branding.accentColor}30`
                            }}
                          >
                            <Building2 className="w-3.5 h-3.5" />
                            {getDepartmentName(selectedEmployee.departmentId)}
                          </Badge>
                        )}
                        {selectedEmployee.siteId && (
                          <Badge 
                            variant="outline" 
                            className="gap-1.5 px-3 py-1"
                          >
                            <MapPin className="w-3.5 h-3.5" />
                            {getSiteName(selectedEmployee.siteId)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="mx-6 border-t border-slate-100" />

                {/* Date & Ancienneté */}
                <div className="px-6 py-4">
                  <div className="flex flex-wrap gap-6 justify-center sm:justify-start">
                    {selectedEmployee.hireDate && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-9 h-9 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${branding.primaryColor}12` }}
                        >
                          <Calendar className="w-4 h-4" style={{ color: branding.primaryColor }} />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-medium">Date d'entrée</p>
                          <p className="text-sm font-semibold text-slate-800">
                            {formatDate(selectedEmployee.hireDate)}
                          </p>
                        </div>
                      </div>
                    )}
                    {selectedEmployee.hireDate && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-9 h-9 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${branding.accentColor}15` }}
                        >
                          <Clock className="w-4 h-4" style={{ color: branding.accentColor }} />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-medium">Ancienneté</p>
                          <p className="text-sm font-semibold text-slate-800">
                            {calculateSeniority(selectedEmployee.hireDate) || '-'}
                          </p>
                        </div>
                      </div>
                    )}
                    {selectedEmployee.dateNaissance && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100"
                        >
                          <User className="w-4 h-4 text-slate-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-medium">Date de naissance</p>
                          <p className="text-sm font-semibold text-slate-800">
                            {formatDate(selectedEmployee.dateNaissance)}
                            {calculateAge(selectedEmployee.dateNaissance) && (
                              <span className="text-slate-500 font-normal"> ({calculateAge(selectedEmployee.dateNaissance)} ans)</span>
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Parcours / Bio */}
                {selectedEmployee.bio && (
                  <div className="px-6 pb-4">
                    <div 
                      className="p-4 rounded-xl relative"
                      style={{ backgroundColor: `${branding.primaryColor}06` }}
                    >
                      <div className="absolute top-2 left-2 w-6 h-6 opacity-20 flex items-center justify-center">
                        <span className="text-2xl" style={{ color: branding.primaryColor }}>"</span>
                      </div>
                      <div className="pl-6">
                        <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: branding.primaryColor }}>
                          Parcours
                        </p>
                        <p className="text-slate-700 text-sm leading-relaxed">
                          {selectedEmployee.bio}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Valeurs Clés */}
                {selectedEmployee.valeursCles && selectedEmployee.valeursCles.length > 0 && (
                  <div className="px-6 pb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Star className="w-4 h-4" style={{ color: branding.accentColor }} />
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                        Valeurs clés
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedEmployee.valeursCles.map((valeur, index) => (
                        <Badge 
                          key={index}
                          className="px-3 py-1 text-sm font-medium"
                          style={{ 
                            backgroundColor: `${branding.accentColor}15`,
                            color: branding.accentColor,
                            border: `1px solid ${branding.accentColor}30`
                          }}
                        >
                          {valeur}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Missions */}
                {selectedEmployee.missions && selectedEmployee.missions.length > 0 && (
                  <div className="px-6 pb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="w-4 h-4" style={{ color: branding.primaryColor }} />
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                        Missions au quotidien
                      </p>
                    </div>
                    <div className="space-y-2">
                      {selectedEmployee.missions.slice(0, 4).map((mission, index) => (
                        <div 
                          key={index}
                          className="flex items-start gap-3 p-3 rounded-lg bg-slate-50"
                        >
                          <div 
                            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                            style={{ backgroundColor: branding.primaryColor }}
                          >
                            {index + 1}
                          </div>
                          <p className="text-sm text-slate-700">{mission}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="mx-6 border-t border-slate-100" />

                {/* Contact Section */}
                <div className="px-6 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3">
                    Contact
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedEmployee.email && (
                      <a 
                        href={`mailto:${selectedEmployee.email}`}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group"
                      >
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${branding.primaryColor}12` }}
                        >
                          <Mail className="w-5 h-5" style={{ color: branding.primaryColor }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-slate-500">Email</p>
                          <p className="text-sm font-medium text-slate-800 truncate group-hover:text-slate-900">
                            {selectedEmployee.email}
                          </p>
                        </div>
                      </a>
                    )}
                    {selectedEmployee.phone && (
                      <a 
                        href={`tel:${selectedEmployee.phone}`}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group"
                      >
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${branding.primaryColor}12` }}
                        >
                          <Phone className="w-5 h-5" style={{ color: branding.primaryColor }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-slate-500">Téléphone</p>
                          <p className="text-sm font-medium text-slate-800 group-hover:text-slate-900">
                            {selectedEmployee.phone}
                          </p>
                        </div>
                      </a>
                    )}
                  </div>
                </div>

                {/* LinkedIn */}
                {selectedEmployee.linkedinUrl && (
                  <div className="px-6 pb-4">
                    <a 
                      href={selectedEmployee.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl text-white font-medium transition-all hover:opacity-90"
                      style={{ backgroundColor: '#0A66C2' }}
                    >
                      <Linkedin className="w-5 h-5" />
                      Voir le profil LinkedIn
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                )}

                {/* Footer */}
                <div 
                  className="py-3 px-6 text-center"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  <p className="text-white/90 text-sm font-medium">
                    {branding.companyDisplayName || branding.companyName || 'Entreprise'}
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* QR Code Dialog */}
        <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
          <DialogContent className="sm:max-w-md">
            {qrEmployee && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5" />
                    QR Code - {qrEmployee.firstName} {qrEmployee.lastName}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center py-6">
                  {/* QR Code */}
                  <div 
                    className="p-4 bg-white rounded-xl shadow-lg border-2"
                    style={{ borderColor: branding.primaryColor }}
                  >
                    <QRCodeSVG 
                      value={`${getPublicUrl()}/public/employee/${qrEmployee.id}`}
                      size={200}
                      level="M"
                      includeMargin={true}
                      fgColor={branding.primaryColor}
                    />
                  </div>
                  
                  {/* Employee Info */}
                  <div className="mt-4 text-center">
                    <p className="font-semibold text-slate-900">
                      {qrEmployee.firstName} {qrEmployee.lastName}
                    </p>
                    <p className="text-sm" style={{ color: branding.primaryColor }}>
                      {qrEmployee.jobTitle}
                    </p>
                  </div>
                  
                  {/* Description */}
                  <p className="mt-4 text-sm text-slate-500 text-center">
                    Scannez ce QR code pour accéder au profil public de cet employé
                  </p>
                  
                  {/* Actions */}
                  <div className="flex gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const url = `${getPublicUrl()}/public/employee/${qrEmployee.id}`;
                        window.open(url, '_blank');
                      }}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Aperçu
                    </Button>
                    <Button
                      onClick={() => downloadQR(qrEmployee)}
                      style={{ backgroundColor: branding.primaryColor }}
                      className="text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Department QR Code Dialog */}
        <Dialog open={showDeptQRDialog} onOpenChange={setShowDeptQRDialog}>
          <DialogContent className="sm:max-w-md">
            {deptQRData && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5" />
                    QR Code - {deptQRData.departmentName}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center py-6">
                  {/* QR Code */}
                  <div 
                    className="p-4 bg-white rounded-xl shadow-lg border-2"
                    style={{ borderColor: branding.primaryColor }}
                  >
                    <QRCodeSVG 
                      value={deptQRData.url}
                      size={200}
                      level="M"
                      includeMargin={true}
                      fgColor={branding.primaryColor}
                    />
                  </div>
                  
                  {/* Department Info */}
                  <div className="mt-4 text-center">
                    <p className="font-semibold text-slate-900">
                      Trombinoscope
                    </p>
                    <p className="text-sm" style={{ color: branding.primaryColor }}>
                      {deptQRData.departmentName}
                    </p>
                  </div>
                  
                  {/* Description */}
                  <p className="mt-4 text-sm text-slate-500 text-center">
                    Scannez ce QR code pour afficher le trombinoscope de ce département
                  </p>
                  
                  {/* Actions */}
                  <div className="flex gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => {
                        // Use current origin for the preview URL
                        const previewUrl = `${window.location.origin}/public/department/${deptQRData.departmentId}`;
                        window.open(previewUrl, '_blank');
                      }}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Aperçu
                    </Button>
                    <Button
                      onClick={() => downloadDeptQR(deptQRData.departmentId)}
                      style={{ backgroundColor: branding.primaryColor }}
                      className="text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Share Dialog */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5" style={{ color: branding.primaryColor }} />
                Partager le trombinoscope
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Create New Link Section */}
              <div className="p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Créer un nouveau lien de partage
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="share-title">Titre du partage</Label>
                    <Input
                      id="share-title"
                      value={newShareConfig.title}
                      onChange={(e) => setNewShareConfig(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Trombinoscope"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="share-expires">Expiration (jours)</Label>
                    <Input
                      id="share-expires"
                      type="number"
                      value={newShareConfig.expiresInDays}
                      onChange={(e) => setNewShareConfig(prev => ({ ...prev, expiresInDays: e.target.value }))}
                      placeholder="Illimité"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Filtrer par département</Label>
                    <Select
                      value={newShareConfig.departmentId || 'all'}
                      onValueChange={(value) => setNewShareConfig(prev => ({ ...prev, departmentId: value === 'all' ? '' : value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Tous les départements" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les départements</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Filtrer par site</Label>
                    <Select
                      value={newShareConfig.siteId || 'all'}
                      onValueChange={(value) => setNewShareConfig(prev => ({ ...prev, siteId: value === 'all' ? '' : value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Tous les sites" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les sites</SelectItem>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="share-description">Description (optionnelle)</Label>
                    <Input
                      id="share-description"
                      value={newShareConfig.description}
                      onChange={(e) => setNewShareConfig(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Description visible sur la page partagée"
                    />
                  </div>
                  
                  <div className="md:col-span-2 flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={newShareConfig.showEmail}
                        onCheckedChange={(checked) => setNewShareConfig(prev => ({ ...prev, showEmail: checked }))}
                      />
                      <span className="text-sm">Afficher les emails</span>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={newShareConfig.showPhone}
                        onCheckedChange={(checked) => setNewShareConfig(prev => ({ ...prev, showPhone: checked }))}
                      />
                      <span className="text-sm">Afficher les téléphones</span>
                    </label>
                  </div>
                </div>
                
                <Button
                  onClick={createShareLink}
                  disabled={creatingLink}
                  className="mt-4 text-white"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  {creatingLink ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Link className="w-4 h-4 mr-2" />
                  )}
                  Générer le lien
                </Button>
              </div>
              
              {/* Existing Links Section */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Link className="w-4 h-4" />
                  Liens de partage existants ({shareLinks.length})
                </h3>
                
                {loadingShareLinks ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : shareLinks.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Aucun lien de partage créé
                  </div>
                ) : (
                  <div className="space-y-3">
                    {shareLinks.map((link) => (
                      <div 
                        key={link.id} 
                        className={`p-4 border rounded-lg ${link.isActive ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800">{link.title}</span>
                              <Badge variant={link.isActive ? 'default' : 'secondary'} className="text-xs">
                                {link.isActive ? 'Actif' : 'Désactivé'}
                              </Badge>
                              {link.expiresAt && new Date(link.expiresAt) < new Date() && (
                                <Badge variant="destructive" className="text-xs">Expiré</Badge>
                              )}
                            </div>
                            
                            {link.description && (
                              <p className="text-sm text-slate-500 mt-1">{link.description}</p>
                            )}
                            
                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {link.viewCount || 0} vue{(link.viewCount || 0) > 1 ? 's' : ''}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Créé le {new Date(link.createdAt).toLocaleDateString('fr-FR')}
                              </span>
                              {link.expiresAt && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Expire le {new Date(link.expiresAt).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                              {!link.showEmail && <Badge variant="outline" className="text-xs">Sans email</Badge>}
                              {!link.showPhone && <Badge variant="outline" className="text-xs">Sans téléphone</Badge>}
                            </div>
                            
                            <div className="mt-2 p-2 bg-slate-100 rounded text-xs text-slate-600 font-mono truncate">
                              {getShareLinkUrl(link)}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyShareLink(link)}
                              title="Copier le lien"
                            >
                              {copiedLinkId === link.id ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(getShareLinkUrl(link), '_blank')}
                              title="Ouvrir dans un nouvel onglet"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleShareLink(link.id)}
                              title={link.isActive ? 'Désactiver' : 'Activer'}
                            >
                              {link.isActive ? (
                                <ToggleRight className="w-4 h-4 text-green-600" />
                              ) : (
                                <ToggleLeft className="w-4 h-4 text-slate-400" />
                              )}
                            </Button>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteShareLink(link.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
